import { Check } from "lucide-react";
import { useRef } from "react";
import { useDialogGuard } from "@/hooks/use-dialog-guard";
import { cn } from "@/lib/utils";
import { InlineSvg } from "./inline-svg";

export type ChoiceCardOption = {
  id: string;
  label: string;
  /** Raw SVG markup entered by tenant designers (trusted admins). */
  iconSvg?: string | null;
  disabled?: boolean;
  /**
   * `muted` is the dashed, de-emphasised tile used for an opt-out choice
   * ("No preference") — visibly not one of the real options.
   */
  variant?: "default" | "muted";
};

type CommonProps = {
  options: ChoiceCardOption[];
  /** Disables the whole group. */
  disabled?: boolean;
  /**
   * Draws a check badge on the selected tile. Defaults to on for `multiple`,
   * where the border+ring alone reads as "highlighted" rather than "ticked",
   * and off for `single`, where a radio group needs no extra affordance.
   */
  showCheckIndicator?: boolean;
  /**
   * Keep the 32px icon slot on every tile as soon as ONE option has an icon, so
   * labels share a baseline. Turn off to let icon-less tiles collapse to the
   * label and centre it against their taller row-mates.
   */
  reserveIconSlot?: boolean;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** Id(s) of text explaining the current choice, e.g. a live description panel. */
  "aria-describedby"?: string;
};

type SingleProps = CommonProps & {
  selection?: "single";
  value?: string | null;
  onValueChange: (id: string) => void;
};

type MultipleProps = CommonProps & {
  selection: "multiple";
  value: string[];
  /**
   * `next` is the full selection after the toggle; `toggledId` is the option the
   * user just activated, which callers with exclusivity rules need in order to
   * tell "added a theme" from "removed one".
   */
  onValueChange: (next: string[], toggledId: string) => void;
};

type ChoiceCardsProps = SingleProps | MultipleProps;

const ARROW_KEYS = new Set(["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"]);

/**
 * Compact selectable tiles ("tile-xs") — a 32px icon over a 12px label.
 *
 * One fluid grid rule and no media queries: `auto-fill` at a 104px minimum gives
 * 3 columns on a 390px phone and grows to a single row on a wide container.
 *
 * Two selection models behind one look:
 *   - `single` (default) — a `radiogroup` of `radio` tiles with a roving
 *     tabindex (one tab stop) and arrow-key navigation. Goal types (QL-111).
 *   - `multiple` — a `group` of `checkbox` tiles, each its own tab stop toggled
 *     with Space, no arrow handling. Sustainability product preferences
 *     (QL-142). Any exclusivity between options belongs to the caller.
 *
 * Fully controlled — the caller owns `value` and is responsible for persisting
 * the choice in `onValueChange`; the group holds no state of its own.
 *
 * See DESIGN.md › Components and the Paper boards
 * "12c · Choice Cards · tile-xs — dev handoff" and "4-0" (product preferences).
 */
export function ChoiceCards({
  options,
  value,
  onValueChange,
  selection = "single",
  disabled,
  showCheckIndicator = selection === "multiple",
  reserveIconSlot = true,
  className,
  ...aria
}: ChoiceCardsProps) {
  const tileRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { markDirty } = useDialogGuard();

  const multiple = selection === "multiple";
  const selectedIds = multiple ? (value as string[]) : [];
  const isChecked = (option: ChoiceCardOption) =>
    multiple ? selectedIds.includes(option.id) : option.id === value;

  const isDisabled = (option: ChoiceCardOption) => !!disabled || !!option.disabled;

  // The icon slot is reserved across the group as soon as one option has an
  // icon; with no icons at all it disappears whatever `reserveIconSlot` says.
  const anyIcon = options.some((option) => !!option.iconSvg);
  const keepIconSlot = reserveIconSlot && anyIcon;

  const select = (option: ChoiceCardOption) => {
    markDirty();
    if (!multiple) {
      (onValueChange as SingleProps["onValueChange"])(option.id);
      return;
    }
    const next = selectedIds.includes(option.id)
      ? selectedIds.filter((id) => id !== option.id)
      : [...selectedIds, option.id];
    (onValueChange as MultipleProps["onValueChange"])(next, option.id);
  };

  // Roving tabindex: a radiogroup is a single tab stop, owned by the checked
  // tile and falling back to the first selectable one. A checkbox group has no
  // roving tabindex — every box is reachable with Tab.
  const selected = options.find((option) => option.id === value);
  const rovingId =
    selected && !isDisabled(selected)
      ? selected.id
      : options.find((option) => !isDisabled(option))?.id;

  // Next selectable option in `step` direction, wrapping at the ends and
  // skipping disabled ones. Returns undefined when nothing is selectable.
  const nextEnabled = (from: number, step: number) => {
    const count = options.length;
    for (let hop = 1; hop <= count; hop++) {
      const candidate = options[(((from + step * hop) % count) + count) % count];
      if (candidate && !isDisabled(candidate)) return candidate;
    }
    return undefined;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (multiple || !ARROW_KEYS.has(event.key)) return;
    event.preventDefault();
    const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
    const next = nextEnabled(index, step);
    if (!next) return;
    // Arrow keys move the selection in a radiogroup, so this is an edit too.
    markDirty();
    (onValueChange as SingleProps["onValueChange"])(next.id);
    tileRefs.current[next.id]?.focus();
  };

  return (
    <div
      role={multiple ? "group" : "radiogroup"}
      className={cn(
        "grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(104px,1fr))]",
        className,
      )}
      {...aria}
    >
      {options.map((option, index) => {
        const checked = isChecked(option);
        const tileDisabled = isDisabled(option);
        const muted = option.variant === "muted";
        return (
          // A native <input> can't hold the tile's icon + label markup; button +
          // role is the pairing Radix's own RadioGroupItem uses. The role is
          // computed, so Biome can't see that aria-checked belongs on it.
          // biome-ignore lint/a11y/useAriaPropsSupportedByRole: role is radio|checkbox
          <button
            key={option.id}
            ref={(el) => {
              tileRefs.current[option.id] = el;
            }}
            type="button"
            role={multiple ? "checkbox" : "radio"}
            aria-checked={checked}
            data-state={checked ? "checked" : "unchecked"}
            disabled={tileDisabled}
            tabIndex={multiple || option.id === rovingId ? 0 : -1}
            onClick={() => select(option)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              "relative flex flex-col items-center justify-center gap-1.5 rounded-lg border px-1.5 py-2.5",
              "text-center text-xs font-medium leading-[1.3] transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              muted && "min-h-[52px] border-dashed border-input text-muted-foreground",
              // Hover/press only on unchecked tiles — a muted wash over the
              // selected tile would read as a deselect.
              checked
                ? "border-primary bg-primary/5 font-semibold ring-1 ring-primary dark:bg-primary/15"
                : "bg-card hover:bg-muted/50 active:bg-muted",
            )}
          >
            {checked && showCheckIndicator && (
              <span
                aria-hidden="true"
                className="absolute -top-[7px] -right-[7px] flex h-4 w-4 items-center justify-center rounded-full border-2 border-card bg-primary text-primary-foreground"
              >
                <Check className="h-2 w-2" strokeWidth={2.5} />
              </span>
            )}
            {(keepIconSlot || option.iconSvg) && (
              <InlineSvg
                svg={option.iconSvg}
                className="h-8 w-8 shrink-0 [&_svg]:h-full [&_svg]:w-full"
              />
            )}
            {/*
              Selecting a tile switches the label to semibold, and bold glyphs are
              wider — on a label sitting near the wrap boundary that alone adds a
              line, growing the tile, the grid row, and everything below it. So
              size the label for the SEMIBOLD text in every state: an invisible
              bold twin shares one grid cell with the real label and dictates the
              height, and only the appearance changes on click.
              Both spans are full-width so they wrap at the same measure.
            */}
            <span className="grid w-full items-center">
              <span aria-hidden="true" className="invisible col-start-1 row-start-1 font-semibold">
                {option.label}
              </span>
              <span className="col-start-1 row-start-1">{option.label}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
